import { normalizeMemoBodyHtml } from './memoBody';
import {
  buildMemoAiPortalLookups,
  type MemoAiPortalLookups,
} from './memoAiLookups';
import type { MemoAiStyleReference } from './memoSubmissions';

export type MemoAiDraftInput = {
  notes: string;
  to?: string;
  cc?: string;
  from?: string;
  date?: string;
  /** Posted memos selected as tone/structure references. */
  styleReferences?: MemoAiStyleReference[];
};

export type MemoAiDraftResult = {
  subject: string;
  /** TipTap-ready HTML */
  bodyHtml: string;
  model?: string;
  lookups: MemoAiPortalLookups;
};

export async function draftInternalMemoWithAi(
  input: MemoAiDraftInput,
): Promise<MemoAiDraftResult> {
  const notes = input.notes.trim();
  if (!notes) throw new Error('Enter notes for the AI draft.');

  const proxyKey = import.meta.env.VITE_DEM_EMAIL_PROXY_KEY?.trim();
  if (!proxyKey) {
    throw new Error('VITE_DEM_EMAIL_PROXY_KEY is not configured in this portal build.');
  }

  const lookups = await buildMemoAiPortalLookups(notes);

  // Only send DEM prices when notes actually discuss pricing — avoids packing/process
  // memos absorbing unrelated list prices into the draft.
  const notesWantPrice = /\b(price|pricing|\$|sgd|unit\s*price|sales\s*price|cost)\b/i.test(
    notes,
  );
  const lookupsForAi = notesWantPrice
    ? lookups
    : {
        ...lookups,
        items: lookups.items.map((item) => ({
          ...item,
          sales_price: '',
          price_note: '',
        })),
      };

  const style_references = (input.styleReferences ?? []).slice(0, 3).map((ref) => ({
    ref_no: ref.ref_no,
    subject: ref.subject,
    date: ref.date,
    body: ref.body_plain.slice(0, 1600),
  }));

  const response = await fetch('/memo-ai-api', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-DEM-EMAIL-KEY': proxyKey,
    },
    body: JSON.stringify({
      notes,
      to: input.to?.trim() || '',
      cc: input.cc?.trim() || '',
      from: input.from?.trim() || '',
      date: input.date?.trim() || '',
      lookups: lookupsForAi,
      style_references,
    }),
  });

  const payload = (await response.json().catch(() => null)) as {
    ok?: boolean;
    subject?: string;
    body?: string;
    model?: string;
    error?: string;
  } | null;

  if (!response.ok || !payload || payload.error) {
    throw new Error(payload?.error || `AI draft failed (${response.status}).`);
  }

  const subject = String(payload.subject ?? '').trim();
  const body = String(payload.body ?? '').trim();
  if (!subject || !body) {
    throw new Error('AI draft returned an empty subject or body.');
  }

  return {
    subject,
    bodyHtml: normalizeMemoBodyHtml(body),
    model: typeof payload.model === 'string' ? payload.model : undefined,
    lookups,
  };
}
